import React, { useState, useEffect } from 'react';
import { TrophyIcon, ChartBarIcon, BanknotesIcon, ArrowTrendingDownIcon, ArrowTrendingUpIcon } from '@heroicons/react/24/solid';
import { GameDetails } from '../../shared/interfaces';
import DialogBox from '../common/DialogBox';
import apiService from '../../services/ApiService';
import { apiRoutes } from '../../shared/apiRoutes';
import { resolveMatchWinnerDisplayName } from '../../utils/resolveMatchWinnerDisplayName';
import {
  formatSettlementPartyDisplay,
  parseSettlementPartyFromApi,
} from '../../utils/matchOverSettlementParty';

interface MatchResultsDialogProps {
  isOpen: boolean;
  onClose: () => void;
  gameDetails: GameDetails;
}

interface MatchResultsData {
  winner: string;
  totalTransactions: number;
  totalCredit: number;
  totalDebit: number;
  profitLoss: number;
  isGameOver: boolean;
  settlementPartyLabel: string;
}

const MatchResultsDialog: React.FC<MatchResultsDialogProps> = ({
  isOpen,
  onClose,
  gameDetails
}) => {
  const [resultsData, setResultsData] = useState<MatchResultsData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [tenantName, setTenantName] = useState('P&L');

  useEffect(() => {
    if (isOpen && gameDetails.id) {
      void fetchTenantName();
      fetchMatchResults();
    }
  }, [isOpen, gameDetails.id]);

  const fetchTenantName = async () => {
    try {
      const profileResponse = await apiService.get('/Auth/profile');
      const profileData = (profileResponse as { data?: { tenantName?: string } }).data ?? profileResponse;
      setTenantName((profileData as { tenantName?: string }).tenantName || localStorage.getItem('tenantName') || 'P&L');
    } catch {
      setTenantName(localStorage.getItem('tenantName') || 'P&L');
    }
  };

  const fetchMatchResults = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await apiService.get(apiRoutes.games.matchResults(gameDetails.id));
      const data = response.data || response;

      const winningTeam =
        data.game?.winningTeam ?? data.game?.winner ?? data.winner ?? gameDetails.winner;
      const winnerName = resolveMatchWinnerDisplayName(winningTeam, gameDetails);
      
      const totalCredit = data.totalCredit || 0;
      const totalDebit = data.totalDebit || 0;
      const settlement = parseSettlementPartyFromApi(data as Record<string, unknown>);
      const gameSettlement = parseSettlementPartyFromApi(
        (data.game ?? {}) as Record<string, unknown>,
      );
      const settlementPartyLabel = formatSettlementPartyDisplay(
        settlement.settlementPartyId ??
          gameSettlement.settlementPartyId ??
          gameDetails.settlementPartyId,
        settlement.settlementPartyCode ??
          gameSettlement.settlementPartyCode ??
          gameDetails.settlementPartyCode,
        settlement.settlementPartyName ??
          gameSettlement.settlementPartyName ??
          gameDetails.settlementPartyName,
        tenantName,
      );

      setResultsData({
        winner: winnerName,
        totalTransactions: data.totalTransactions || 0,
        totalCredit,
        totalDebit,
        profitLoss: data.profitLoss || 0,
        isGameOver: data.isGameOver || false,
        settlementPartyLabel,
      });
    } catch (err: any) {
      console.error('Failed to fetch match results:', err);
      setError('Failed to load match results');
      setResultsData({
        winner: resolveMatchWinnerDisplayName(gameDetails.winner, gameDetails),
        totalTransactions: 0,
        totalCredit: 0,
        totalDebit: 0,
        profitLoss: 0,
        isGameOver: gameDetails.isGameOver || false,
        settlementPartyLabel: formatSettlementPartyDisplay(
          gameDetails.settlementPartyId,
          gameDetails.settlementPartyCode,
          gameDetails.settlementPartyName,
          tenantName,
        ),
      });
    } finally {
      setLoading(false);
    }
  };

  const getProfitLossTone = (amount: number) => {
    if (amount > 0) return 'pos';
    if (amount < 0) return 'neg';
    return 'zero';
  };

  const getProfitLossSymbol = (amount: number) => {
    if (amount > 0) return '+';
    if (amount < 0) return '-';
    return '';
  };

  const formatNumber = (num: number) => {
    return num.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  if (!isOpen) return null;

  return (
    <div className="mt-4">
      <DialogBox isOpen={isOpen} onClose={onClose} title="Match Results" panelClassName="max-w-3xl w-[90vw]">
        <div className="match-results p-6 min-h-[280px]">
          {/* Match completed header */}
          <div className="text-center mb-6">
            <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-gradient-to-br from-amber-400 to-amber-600 shadow-lg mb-3">
              <TrophyIcon className="h-8 w-8 text-white" />
            </div>
            <h2 className="match-results-title text-xl font-bold mb-1">Match Completed</h2>
            <p className="match-results-muted text-sm font-medium">
              {gameDetails.teamA} vs {gameDetails.teamB}
              {gameDetails.teamC && ` vs ${gameDetails.teamC}`}
            </p>
          </div>

          {loading && (
            <div className="flex flex-col justify-center items-center py-12">
              <div className="animate-spin rounded-full h-10 w-10 border-2 border-blue-500 border-t-transparent mb-3"></div>
              <span className="match-results-muted text-sm font-medium">Loading results...</span>
            </div>
          )}

          {error && (
            <div className="match-results-error rounded-lg px-4 py-3 mb-4">
              <p className="text-sm">{error}</p>
            </div>
          )}

          {resultsData && !loading && (
            <div className="space-y-4">
              <div className="match-results-card match-results-card-winner relative rounded-xl p-4 overflow-hidden">
                <div className="flex flex-col items-center py-2">
                  <span className="match-results-label text-xs font-semibold uppercase tracking-wider mb-1">
                    {resultsData.winner === 'Tie' || resultsData.winner === 'Abandoned' ? 'Result' : 'Winner'}
                  </span>
                  <p className="match-results-value text-lg font-bold">{resultsData.winner}</p>
                </div>
              </div>

              {resultsData.winner !== 'Tie' && resultsData.winner !== 'Abandoned' && (
              <>
              <div className="match-results-card match-results-card-settlement rounded-lg p-4">
                <span className="match-results-label text-xs font-semibold uppercase">Settlement party</span>
                <p className="match-results-value text-base font-bold mt-1">{resultsData.settlementPartyLabel}</p>
              </div>

              <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                <div className="match-results-card match-results-card-tx rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-8 h-8 rounded-lg bg-blue-500 flex items-center justify-center">
                      <ChartBarIcon className="h-4 w-4 text-white" />
                    </div>
                    <span className="match-results-label text-xs font-semibold uppercase">Transactions</span>
                  </div>
                  <p className="match-results-value text-lg font-bold">{formatNumber(resultsData.totalTransactions)}</p>
                </div>

                <div className="match-results-card match-results-card-payable rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-8 h-8 rounded-lg bg-red-500 flex items-center justify-center">
                      <ArrowTrendingDownIcon className="h-4 w-4 text-white" />
                    </div>
                    <span className="match-results-label text-xs font-semibold uppercase">Payable</span>
                  </div>
                  <p className="match-results-amount text-lg font-bold">{formatNumber(resultsData.totalDebit / 100)}</p>
                </div>

                <div className="match-results-card match-results-card-receivable rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-8 h-8 rounded-lg bg-teal-500 flex items-center justify-center">
                      <ArrowTrendingUpIcon className="h-4 w-4 text-white" />
                    </div>
                    <span className="match-results-label text-xs font-semibold uppercase">Receivable</span>
                  </div>
                  <p className="match-results-amount text-lg font-bold">{formatNumber(resultsData.totalCredit / 100)}</p>
                </div>

                {(() => {
                  const tone = getProfitLossTone(resultsData.profitLoss);
                  return (
                    <div className={`match-results-card match-results-card-profit match-results-card-profit-${tone} rounded-lg p-4`}>
                      <div className="flex items-center gap-2 mb-2">
                        <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${tone === 'neg' ? 'bg-red-500' : tone === 'pos' ? 'bg-emerald-500' : 'bg-slate-500'}`}>
                          <BanknotesIcon className="h-4 w-4 text-white" />
                        </div>
                        <span className="match-results-label text-xs font-semibold uppercase">Profit/Loss</span>
                      </div>
                      <p className="match-results-amount text-lg font-bold">
                        {getProfitLossSymbol(resultsData.profitLoss)}{formatNumber(Math.abs(resultsData.profitLoss / 100))}
                      </p>
                    </div>
                  );
                })()}
              </div>
              </>
              )}
            </div>
          )}

          <div className="flex justify-center mt-6">
            <button
              onClick={onClose}
              className="px-6 py-2.5 bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white rounded-lg text-sm font-semibold shadow-md transition-all duration-200"
            >
              Close
            </button>
          </div>
        </div>
      </DialogBox>
    </div>
  );
};

export default MatchResultsDialog;
